/**
 * «Приход ТМЦ» — в одной манере со справочниками Экономиста и «Техникой»:
 * панель инструментов над таблицей, строка открывается в боковой панели.
 * Ширина реагирует на контейнер (`receipts`), а не на окно.
 */
import styled from 'styled-components';
import { fleetLeading, fleetType } from '@entities/fleet';
import { Select } from '@shared/ui/Input';
import { riseIn } from '@shared/theme/animations';

export const NARROW = '640px';

export const Root = styled.div`
  container: receipts / inline-size;
  animation: ${riseIn} 320ms ${({ theme }) => theme.motion.easeOut} both;
`;

export const Lead = styled.p`
  max-width: 880px;
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

/** Вкладки слева, «Загрузить книгу» справа; на узком — друг под другом. */
export const TabsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};

  & > .spacer {
    flex: 1 1 auto;
  }
`;

export const PanelBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

/** Фильтры и действия — над таблицей. */
export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  font-size: ${fleetType.body};

  & > .search {
    flex: 0 1 300px;
    min-width: 200px;
  }

  & > select {
    width: auto;
    min-width: 120px;
  }

  & > .spacer {
    flex: 1 1 auto;
  }

  @container receipts (max-width: ${NARROW}) {
    & > .search,
    & > select,
    & > button {
      flex: 1 1 100%;
    }

    & > .spacer {
      display: none;
    }
  }
`;

/** Итоги по фильтру: строк, без НДС, НДС, с НДС. */
export const Totals = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(5)}`};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;

  & b {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

export const TableBox = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

export const Table = styled.table`
  width: 100%;
  min-width: 860px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  thead th {
    position: sticky;
    top: 0;
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
    background: ${({ theme }) => theme.colors.backgroundElevated};
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    text-align: left;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  th.num,
  td.num {
    text-align: right;
  }

  td {
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  td.num {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  td.nowrap {
    white-space: nowrap;
  }

  td .sub {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    overflow-wrap: anywhere;
  }

  td .warn {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.status.warning};
  }

  tbody tr:last-child > td {
    border-bottom: none;
  }

  tbody tr[data-open='true'] {
    cursor: pointer;
  }

  tbody tr:hover > td,
  tbody tr:focus-within > td {
    background: ${({ theme }) => theme.colors.surface};
  }

  tfoot td {
    border-top: 1px solid ${({ theme }) => theme.colors.borderStrong};
    border-bottom: none;
    font-weight: 600;
  }
`;

/** Свод «объект × месяц»: первая колонка закреплена при прокрутке вбок. */
export const Matrix = styled(Table)`
  min-width: 1100px;

  th:first-child,
  td:first-child {
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: 220px;
    max-width: 280px;
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }

  thead th:first-child {
    z-index: 2;
  }

  td.empty {
    color: ${({ theme }) => theme.colors.text.muted};
  }

  tr.group > td {
    background: ${({ theme }) => theme.colors.surface};
    font-weight: 600;
  }
`;

export const KeyButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: 600;
  text-align: left;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:focus-visible {
    outline: none;
    border-radius: ${({ theme }) => theme.radius.xs};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const EmptyCard = styled.div`
  max-width: 720px;
  padding: ${({ theme }) => theme.spacing(6)};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.lg};

  & > h3 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.lead};
    font-weight: 600;
    text-wrap: balance;
  }

  & > p {
    margin: 0;
    font-size: ${fleetType.base};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > .actions {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(2)};
    margin-top: ${({ theme }) => theme.spacing(4)};
  }
`;

export const FlashBox = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

export const Pager = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

/** Блок предпросмотра загрузки: заголовок и содержимое. */
export const Section = styled.section`
  margin-top: ${({ theme }) => theme.spacing(5)};

  & > h3 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.base};
    font-weight: 600;
  }

  & > p {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.body};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & ul {
    margin: 0;
    padding-left: ${({ theme }) => theme.spacing(5)};
    font-size: ${fleetType.body};
    line-height: ${fleetLeading.normal};
  }
`;

/** Журнал правок: запись — кто и когда, ниже поля «было → стало». */
export const HistoryList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: ${fleetType.body};

  & > li {
    padding: ${({ theme }) => `${theme.spacing(2)} 0`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  & > li:last-child {
    border-bottom: none;
  }

  & .who {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${fleetType.small};
  }

  & .change {
    margin-top: ${({ theme }) => theme.spacing(1)};
    overflow-wrap: anywhere;
  }

  & del {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

/** Выбор в ячейке таблицы: шириной с подпись, а не с колонку. */
export const CellSelect = styled(Select)<{ $min: number }>`
  min-width: ${({ $min }) => $min}px;
`;
